import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/shared/Sidebar';
import Header from '../../components/shared/Header';
import UserMetricCard from '../../components/users/UserMetricCard';
import UserTable from '../../components/users/UserTable';
import UserFilters from '../../components/users/UserFilters';
import { apiService, User, ApiError } from '../../services/api';
import './Users.css';

const Users: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [roleFilter, setRoleFilter] = useState('All Roles');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Fetch users from API
  useEffect(() => {
    const abortController = new AbortController();

    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getAllUsers(abortController.signal);
        
        if (response.users && Array.isArray(response.users)) {
          // Map API users to frontend user format
          const mappedUsers = response.users.map(apiUser => 
            apiService.mapApiUserToUser(apiUser)
          );
          setUsers(mappedUsers);
        } else {
          setError('Invalid response format from server');
        }
      } catch (err: any) {
        // Ignore aborted requests
        if (err.name === 'AbortError') return;
        
        // Handle specific error types
        if (err instanceof ApiError) {
          if (err.status === 403) {
            setError('You do not have permission to view users. Please contact your administrator.');
          } else if (err.status === 401) {
            setError('Your session has expired. Please sign in again.');
          } else {
            setError(err.message || 'An error occurred while fetching users');
          }
        } else {
          setError(err.message || 'An error occurred while fetching users');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();

    // Cleanup: cancel request if component unmounts
    return () => {
      abortController.abort();
    };
  }, []);

  const usersPerPage = 8;

  // Filter users based on search, status, and role
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.email && user.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (user.phone && user.phone.includes(searchQuery));

    const matchesStatus = statusFilter === 'All Status' || user.status === statusFilter.toUpperCase();
    const matchesRole = roleFilter === 'All Roles' || user.role.toLowerCase() === roleFilter.toLowerCase();

    return matchesSearch && matchesStatus && matchesRole;
  });

  // Calculate metrics from filtered users
  const totalUsers = filteredUsers.length;
  const activeUsers = filteredUsers.filter(u => u.status === 'ACTIVE').length;
  const restaurantOwners = filteredUsers.filter(u => u.role.toLowerCase() === 'owner' || u.role.toLowerCase() === 'restaurant owner').length;
  const blockedUsers = filteredUsers.filter(u => u.status === 'BLOCKED').length;

  // Paginate filtered users
  const totalPages = Math.ceil(totalUsers / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, roleFilter]);

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === paginatedUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(paginatedUsers.map(u => u.id));
    }
  };

  const handleExportUsers = () => {
    // Export filtered users to CSV
    const usersToExport = filteredUsers;
    
    if (usersToExport.length === 0) {
      alert('No users to export');
      return;
    }

    // Create CSV headers
    const headers = ['User ID', 'Name', 'Email', 'Phone', 'Role', 'Status', 'Last Login'];
    
    // Create CSV rows
    const rows = usersToExport.map(user => [
      user.id,
      user.name,
      user.email || '',
      user.phone || '',
      user.role,
      user.status,
      user.lastLogin
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleAddUser = () => {
    // Navigate to add user page or open add user modal
    console.log('Add new user');
  };

  return (
    <div className="dashboard-container">
      <Sidebar 
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
      />
      <div className={`dashboard-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header page="users" />
        <main className="dashboard-content">
          <div className="users-header-section">
            <div>
              <h1 className="users-title">Users</h1>
              <p className="users-subtitle">Manage user accounts and permissions.</p>
            </div>
            <div className="users-header-actions">
              <button className="users-export-btn" onClick={handleExportUsers}>
                <span>📥</span> Export CSV
              </button>
              <button className="users-add-btn" onClick={handleAddUser}>
                + Add User
              </button>
            </div>
          </div>

          <div className="users-metrics">
            <UserMetricCard
              title="Total Users"
              value={totalUsers.toLocaleString()}
              color="default"
            />
            <UserMetricCard
              title="Active Users"
              value={activeUsers.toLocaleString()}
              color="green"
            />
            <UserMetricCard
              title="Restaurant Owners"
              value={restaurantOwners.toLocaleString()}
              color="blue"
            />
            <UserMetricCard
              title="Blocked Users"
              value={blockedUsers.toLocaleString()}
              color="red"
            />
          </div>

          <UserFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            roleFilter={roleFilter}
            onRoleFilterChange={setRoleFilter}
          />

          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Loading users...</p>
            </div>
          ) : error ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
              <p>Error: {error}</p>
            </div>
          ) : (
            <UserTable
              users={paginatedUsers}
              selectedUsers={selectedUsers}
              onSelectUser={handleSelectUser}
              onSelectAll={handleSelectAll}
            />
          )}

          {!loading && !error && totalUsers > 0 && (
            <div className="users-pagination">
              <div className="pagination-info">
                Showing {((currentPage - 1) * usersPerPage) + 1} to {Math.min(currentPage * usersPerPage, totalUsers)} of {totalUsers} users
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  className="pagination-btn"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          {!loading && !error && totalUsers === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>No users found.</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Users;

